import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import { getSize } from '../../util/getSize.js';
import { getValueIf } from '../../util/getValueIf.js';
import { extractPaddings } from '../../util/spacing.js';
import '../Box/index.js';
import '../Icon/index.js';
import '../Image/index.js';
import { Link as LinkMemoized } from '../Link/Link.js';
import '../Typography/index.js';
import { getImageBorderRadius, getIconAssetColor } from './ListItem.mappers.js';
import { Typography } from '../Typography/Typography.js';
import { Box } from '../Box/Box.js';
import { Image as ImageMemoized } from '../Image/Image.js';
import { Icon } from '../Icon/Icon.js';

var _excluded = ["as", "asset", "alignAsset", "control", "alignControl", "title", "subTitle", "onClick", "href", "disabled", "cursor"];
function isImage(media) {
  return !!(media && 'src' in media);
}

/**
 * @deprecated ListItem is deprecated and being replaced by ListItemV2. */
var ListItem = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var as = _ref.as,
    asset = _ref.asset,
    alignAsset = _ref.alignAsset,
    _ref$control = _ref.control,
    controlElement = _ref$control === void 0 ? null : _ref$control,
    alignControl = _ref.alignControl,
    title = _ref.title,
    subTitle = _ref.subTitle,
    onClick = _ref.onClick,
    href = _ref.href,
    disabled = _ref.disabled,
    cursor = _ref.cursor,
    restProps = _objectWithoutProperties(_ref, _excluded);
  var _extractPaddings = extractPaddings(restProps),
    paddings = _extractPaddings.paddings,
    rest = _extractPaddings.rest;
  var subTitleElement = React.useMemo(function () {
    var isString = typeof subTitle === 'string';
    var content = isString ? subTitle : subTitle === null || subTitle === void 0 ? void 0 : subTitle.children;
    var variant = !isString && (subTitle === null || subTitle === void 0 ? void 0 : subTitle.variant) || 'body-s';
    if (!content) return null;
    return /*#__PURE__*/React.createElement(Typography, {
      variant: variant,
      textColor: disabled ? 'base.disabled' : 'base.subtle',
      mt: "4",
      lineClamp: 1
    }, content);
  }, [subTitle, disabled]);
  var imageAsset = React.useMemo(function () {
    var _asset$alt;
    if (!asset || !isImage(asset)) return null;
    var defaultHeightWidthValue = '48px';
    var heightProvided = asset === null || asset === void 0 ? void 0 : asset.height;
    var height = getSize(heightProvided || 'xl3') || defaultHeightWidthValue;
    var heightNumber = parseInt(height, 10);
    var borderRadius = getImageBorderRadius(heightNumber);
    return /*#__PURE__*/React.createElement(Box, _extends({
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    }, !heightProvided && {
      height: height,
      width: defaultHeightWidthValue,
      borderRadius: '8'
    }, {
      overflow: "hidden"
    }), /*#__PURE__*/React.createElement(ImageMemoized, {
      src: asset.src,
      alt: (_asset$alt = asset.alt) !== null && _asset$alt !== void 0 ? _asset$alt : '' // to make it decorative
      ,
      objectFit: "cover",
      loading: "lazy",
      height: getValueIf(!!heightProvided, height),
      disabled: disabled,
      responsive: "manual",
      requestWidth: heightNumber,
      borderRadius: getValueIf(!!heightProvided, borderRadius)
    }));
  }, [asset, disabled]);
  var assetElement = asset && (isImage(asset) ? imageAsset : /*#__PURE__*/React.createElement(Icon, {
    id: asset.id,
    size: asset.size || '16',
    color: getIconAssetColor(disabled, asset === null || asset === void 0 ? void 0 : asset.color)
  }));
  var titleElement = React.useMemo(function () {
    var isString = typeof title === 'string';
    var content = isString ? title : title.children;
    return /*#__PURE__*/React.createElement(Typography, {
      lineClamp: 1,
      tag: "span",
      variant: isString ? 'body-default' : title.variant
    }, content);
  }, [title]);
  var contentElement = function contentElement() {
    var content = /*#__PURE__*/React.createElement(Box, _extends({
      display: "flex",
      alignItems: "center",
      backgroundColor: "base.transparent",
      cursor: cursor || 'default'
    }, paddings), alignControl === 'start' && controlElement && /*#__PURE__*/React.createElement(Box, {
      pe: "12"
    }, controlElement), alignAsset === 'start' && assetElement && /*#__PURE__*/React.createElement(Box, {
      pe: "12"
    }, assetElement), /*#__PURE__*/React.createElement(Box, {
      display: "flex",
      flexDirection: "column",
      me: "12",
      flexGrow: 1
    }, titleElement, subTitleElement), alignControl === 'end' && controlElement, alignAsset === 'end' && assetElement);
    if (!disabled && (href || onClick)) {
      return /*#__PURE__*/React.createElement(LinkMemoized, {
        tabIndex: 0,
        href: href,
        onClick: onClick,
        display: "block",
        width: "100%",
        interactive: "product.primary"
      }, content);
    }
    if (controlElement && as !== 'default') {
      // eslint-disable-next-line jsx-a11y/label-has-associated-control
      return /*#__PURE__*/React.createElement("label", null, content);
    }
    return content;
  };
  return /*#__PURE__*/React.createElement(Box, _extends({}, rest, {
    as: "li",
    disabled: disabled,
    textColor: disabled ? 'base.disabled' : undefined,
    "aria-disabled": disabled && 'true',
    ref: ref
  }), contentElement());
});
ListItem.displayName = 'ListItem';
ListItem.defaultProps = {
  as: 'label',
  p: '16',
  alignAsset: 'start',
  alignControl: 'start'
};

export { ListItem };
//# sourceMappingURL=ListItem.js.map
